import { BUTTON_SIZE, COLORS, DSButton } from '@repo/design-system/index'
import { QIcon_check_default } from '@repo/icons/index'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { ai_tutor_apis } from '../../../modules/apis'
import { axiosGet, axiosPatch } from '../../../modules/axiosHelper'
import { n_membership, s_pricing } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import BottomSheet from '../pricing/Pricing/BottomSheet'

interface PendingAITutorMembership {
  id: number
  name: string
  paid_at: string
  num_days: number
}

interface PendingAITutorMembershipResponse {
  user_ai_tutor_permissions: PendingAITutorMembership[]
  success: boolean
}

const patchAITutorMembershipActivation = async (id) => {
  const response = await axiosPatch(ai_tutor_apis.permission.activate(id))
  return response
}
const getPendingAITutorMembership = async (): Promise<PendingAITutorMembershipResponse> => {
  const response = await axiosGet(ai_tutor_apis.permission.pendingList())
  return response
}

export const usePendingAITutorMembership = (
  config: UseQueryOptions<PendingAITutorMembershipResponse, AxiosError> = {}
) => {
  return useQuery({
    queryKey: ['pendingAITutorMembership'],
    queryFn: () => getPendingAITutorMembership(),
    ...config,
  })
}

const AITutorActivationDialog = ({ isOpen, onClose, onSuccess }) => {
  const handleSuccess = () => {
    onSuccess && onSuccess()
    onClose()
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <AITutorActivationBody onSuccess={handleSuccess} />
    </BottomSheet>
  )
}

const AITutorActivationBody = ({ onSuccess }) => {
  const [membershipId, setMembershipId] = useState(null)
  const [isActivationLoading, setIsActivationLoading] = useState(false)

  const { t } = useTranslation()
  const { data } = usePendingAITutorMembership()
  const pendingMemberships = data?.user_ai_tutor_permissions

  const handleMembershipClick = (id) => {
    setMembershipId(id)
  }

  const handleActivationClick = async (id) => {
    if (!id) return

    try {
      setIsActivationLoading(true)
      await patchAITutorMembershipActivation(id)
      onSuccess()
    } catch (error) {
      console.error('Activation failed:', error)
    } finally {
      setIsActivationLoading(false)
    }
  }

  return (
    <section>
      <header className="pt-16 pb-40 flex items-center justify-center">
        <h1 className="Headline2-24Bd text-black text-center">{t(n_membership.activation.title)}</h1>
      </header>
      <main className="flex flex-col items-center">
        <h2 className="Body2-14Md text-gray-500">{t(n_membership.activation.subtitle(1))}</h2>
        <ul className="px-32 pt-16 pb-[64px] w-full flex flex-col gap-y-16">
          {pendingMemberships?.map((membership) => (
            <li
              key={membership.id}
              className={`p-16 w-full border rounded-lg ${
                membershipId === membership.id ? 'bg-gray-50 border-purple-200 shadow' : 'bg-white border-gray-200'
              }`}
            >
              <button
                className="w-full flex justify-between items-center"
                onClick={() => handleMembershipClick(membership.id)}
              >
                <div className="flex flex-col gap-y-8 items-start">
                  <span className="Caption-12Md text-gray-500">{membership.paid_at}</span>
                  <span className="Title2-18Bd text-black">{membership.name}</span>
                  <span className="Body2-14Md text-gray-600">{t(s_pricing.day(membership.num_days))}</span>
                </div>
                <div>
                  {membershipId === membership.id && (
                    <QIcon_check_default width={24} height={24} color={COLORS.PURPLE500} />
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </main>
      <div className="sticky bottom-0 left-0 w-full bg-white px-16 pb-40 pt-8">
        <DSButton
          size={BUTTON_SIZE.LG}
          customBgColor={COLORS.PURPLE500}
          isLoading={isActivationLoading}
          disabled={!membershipId}
          onClick={() => handleActivationClick(membershipId)}
        >
          <DSButton.Label customTxtColor="white">{t(n_membership.activation.btn)}</DSButton.Label>
        </DSButton>
      </div>
    </section>
  )
}

export default AITutorActivationDialog
